import { getWindArrow } from 'features/Weather/utils/windArrow.utils';
import { DateToString } from 'utils/strings/string.utils';
import {
  buildForecastChartOptions,
  buildMetricChartOptions,
  buildWindSpeedChartOptions,
  formatForecastChartLabel,
} from './forecastChartOptions.utils';

// These generators return the dataset format consumed by createChartOptionFromDataSets.
// Keep each forecast's data mapping here; axes, labels, and plugins live in the options utility.

/** Build configured metric charts, including the high/average/low temperature chart. */
export const generateMetricChart = (forecast, { chartTitle, chartType, datasets }) => ({
  dataSets: {
    labels: forecast.map(formatForecastChartLabel),
    datasets: datasets.map(({ datasetLabel, getValue, color, backgroundColor, fill }) => ({
      type: chartType,
      label: datasetLabel,
      data: forecast.map(getValue),
      [chartType === 'bar' ? 'backgroundColor' : 'borderColor']: color,
      ...(backgroundColor ? { backgroundColor } : {}),
      ...(fill === undefined ? {} : { fill }),
    })),
  },
  options: buildMetricChartOptions(chartTitle),
});

/** Add direction arrows to the configured wind-speed line and fill. */
export const generateWindSpeedChart = (forecast, chartConfig) => {
  const chart = generateMetricChart(forecast, chartConfig);
  const windDirectionDatasetIndex = chart.dataSets.datasets.length;

  return {
    dataSets: {
      ...chart.dataSets,
      datasets: [
        ...chart.dataSets.datasets,
        {
          type: 'line',
          label: 'Wind Direction',
          data: forecast.map((item) => ({
            y: item.wind_speed,
            windDirection: item.wind_dir,
            displayValue: `${getWindArrow(item.wind_dir)} ${item.wind_dir}°`,
          })),
          borderColor: 'rgba(0,0,0,0)',
          showLine: false,
          showSymbol: false,
        },
      ],
    },
    options: buildWindSpeedChartOptions(chart.options, windDirectionDatasetIndex),
  };
};

/** Build the 5-day chart from morning, afternoon, evening, and overnight forecasts. */
export const generateDetailedChart = (
  forecast,
  pluginOptions,
  weatherIconStyle = { color: 'black' },
  windIconStyle = { color: 'black' },
  textFillStyle = 'black',
) => {
  const datasets = [
    {
      type: 'line',
      label: 'Temperature',
      data: forecast.map((item, index) => ({
        value: item.temperature,
        key: index,
        title: `${item.weekday} ${item.day_segment}, ${new Date(item.iso8601).toLocaleTimeString()}`,
        beaufort: item.beaufort_desc,
        temperature: `${item.temperature}°F`,
        ...getForecastTooltipData(item),
      })),
      borderColor: 'red',
      yAxisID: 'y',
    },
    buildDewPointDataset(forecast),
    buildPrecipitationDataset(forecast),
    buildWindDirectionDataset(forecast),
  ];

  return {
    dataSets: { datasets },
    options: buildForecastChartOptions(forecast, {
      period: 'detailed',
      datasets,
      pluginOptions,
      weatherIconStyle,
      windIconStyle,
      textFillStyle,
    }),
  };
};

/** Build the 7-day chart, keeping weather-icon and wind-arrow spacing independent. */
export const generate7DayChart = (
  forecast,
  pluginOptions,
  weatherIconSkip = 0,
  windDirectionIconSkip = 0,
  weatherIconStyle = { color: 'black' },
  windIconStyle = { color: 'black' },
  textFillStyle = 'black',
) => {
  const datasets = [
    {
      type: 'line',
      label: 'Temperature',
      data: forecast.map((item, index) => ({
        value: item.temperature,
        key: index,
        title: getForecastDayTitle(item, item.weekday_local),
        beaufort: item.description,
        temperature: `${item.temperature}°F`,
        ...getForecastTooltipData(item),
      })),
      borderColor: 'red',
      yAxisID: 'y',
      xAxisID: 'x',
    },
    { ...buildDewPointDataset(forecast), xAxisID: 'x' },
    { ...buildPrecipitationDataset(forecast), xAxisID: 'xBar' },
    { ...buildWindDirectionDataset(forecast), xAxisID: 'xSkip' },
  ];

  return {
    dataSets: { datasets },
    options: buildForecastChartOptions(forecast, {
      period: 'hourly',
      datasets,
      pluginOptions,
      weatherIconStyle,
      windIconStyle,
      textFillStyle,
      weatherIconSkip,
      windDirectionIconSkip,
    }),
  };
};

/** Build the 15-day chart with high/low lines and weather icons at their midpoint. */
export const generate15DayChart = (
  forecast,
  pluginOptions,
  weatherIconStyle = { color: 'black' },
  windIconStyle = { color: 'black' },
  textFillStyle = 'black',
) => {
  const datasets = [
    {
      type: 'line',
      label: 'Temperature',
      data: forecast.map((item, index) => ({
        value: (parseFloat(item.low_temp) + parseFloat(item.high_temp)) / 2,
        key: index,
        title: getForecastDayTitle(item, item.weekday),
        beaufort: item.beaufort_desc,
        temperature: `${item.high_temp}°F / ${item.low_temp}°F`,
        ...getForecastTooltipData(item),
      })),
      borderColor: 'rgba(0,0,0,0)',
      yAxisID: 'y',
    },
    buildDewPointDataset(forecast),
    {
      type: 'line',
      label: 'High Temp',
      data: forecast.map((item, index) => ({ value: item.high_temp, key: index })),
      borderColor: 'red',
      yAxisID: 'y',
    },
    {
      type: 'line',
      label: 'Low Temp',
      data: forecast.map((item, index) => ({ value: item.low_temp, key: index })),
      borderColor: 'blue',
      yAxisID: 'y',
    },
    buildPrecipitationDataset(forecast),
    buildWindDirectionDataset(forecast),
  ];

  return {
    dataSets: { datasets },
    options: buildForecastChartOptions(forecast, {
      period: 'daily',
      datasets,
      pluginOptions,
      weatherIconStyle,
      windIconStyle,
      textFillStyle,
    }),
  };
};

const getRainfallAmount = (rainfall) => (rainfall === '*' ? 0 : parseFloat(rainfall));

const getForecastDayTitle = (forecast, weekday) => {
  const date = new Date(forecast.iso8601);
  return `${weekday} ${DateToString(date).day}, ${date.toLocaleTimeString()}`;
};

const getForecastTooltipData = (forecast) => ({
  icon: forecast.icon,
  windSpeed: forecast.wind_speed,
  dewPoint: forecast.dew_point,
  // The chart adapter's tooltip contract calls the rainfall amount "precipProb".
  precipProb: getRainfallAmount(forecast.rainfall),
  windDirection: forecast.wind_long,
});

const buildDewPointDataset = (forecast) => ({
  type: 'line',
  label: 'Dew Point',
  data: forecast.map((item, index) => ({ value: item.dew_point, key: index })),
  borderColor: 'green',
  borderDash: [5],
  yAxisID: 'y',
});

const buildPrecipitationDataset = (forecast) => ({
  type: 'bar',
  label: 'Precipitation',
  data: forecast.map((item, index) => ({ value: getRainfallAmount(item.rainfall), key: index })),
  backgroundColor: 'lightblue',
  yAxisID: 'yPrecip',
});

const buildWindDirectionDataset = (forecast) => ({
  type: 'line',
  label: 'Wind Direction',
  // A constant value positions the arrows; the direction remains available to the plugin.
  data: forecast.map((item, index) => ({ value: 1, key: index, windDirection: item.wind_dir })),
  borderColor: 'rgba(0,0,0,0)',
});
